import React from 'react';
import {
  notEmptyString,
} from '@populateui/utils';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import ChartCard from 'core/components/Charts/ChartCard';
import EventTimeline from 'core/components/Charts/EventTimeline';
import TrendChart from 'core/components/Charts/TrendChart';
import TrendTooltip from 'core/components/Charts/TrendTooltip';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  RTMPatientTrendResponseDTO,
} from 'dtos';
import {
  ADHERENCE_RANK_LABELS,
  ADHERENCE_REFERENCE_Y,
  ADHERENCE_Y_DOMAIN,
  ADHERENCE_Y_TICKS,
  CONDITION_RANK_LABELS,
  CONDITION_REFERENCE_Y,
  CONDITION_Y_DOMAIN,
  CONDITION_Y_TICKS,
  METRIC_TONE_BADGE_CLASSES,
  MetricTheme,
  MetricTone,
  PAIN_ITEM_SERIES,
  PAIN_Y_DOMAIN,
  PAIN_Y_TICKS,
  TREND_CHART_COLORS,
} from 'pages/Dashboard/pages/Dashboard/RTM/Trends/constants';
import {
  getAdherenceTone,
  getConditionTone,
  getEventTimelineItems,
  getLastTwoNumericValues,
  getPainTone,
  getSeriesTrendLabel,
  TrendChartRow,
} from 'pages/Dashboard/pages/Dashboard/RTM/Trends/helpers';
import {
  formatDate,
} from 'utils/date';

export type PainMode = 'composite' | 'items';

type Props = {
  chartRows: TrendChartRow[];
  patientTrend?: RTMPatientTrendResponseDTO;
  painMode: PainMode;
  onPainModeChange: (mode: PainMode) => void;
};

type PainModeToggleProps = {
  painMode: PainMode;
  onPainModeChange: (mode: PainMode) => void;
};

type ChartStatusPillsProps = {
  currentLabel: string;
  trendLabel: string;
  theme: MetricTheme;
  tone: MetricTone;
};

type SuddenWorseningStatCardProps = {
  header: string;
  value: string;
};

function SuddenWorseningStatCard({
  header,
  value,
}: SuddenWorseningStatCardProps) {
  return (
    <div className="rounded-md border border-gray-200/70 bg-gray-100 px-3.5 py-1 opacity-90">
      <div className="mt-1 text-base font-semibold tracking-tight text-gray-700">
        {value}
      </div>
      <div className="text-[11px] font-medium text-gray-500">
        {header}
      </div>
    </div>
  );
}

function ChartStatusPills({
  currentLabel,
  // trendLabel,
  theme,
  tone,
}: ChartStatusPillsProps) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span
        className={clsx(
          'inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium',
          METRIC_TONE_BADGE_CLASSES[theme][tone],
        )}
      >
        {`Current: ${currentLabel}`}
      </span>
      {/* <span
        className={clsx(
          'inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium',
          METRIC_TONE_BADGE_CLASSES[theme][tone],
        )}
      >
        {`Trend: ${trendLabel}`}
      </span> */}
    </div>
  );
}

function PainModeToggle({
  painMode,
  onPainModeChange,
}: PainModeToggleProps) {
  return (
    <div className="rtm-pain-mode-controls flex flex-wrap items-center justify-end gap-2">
      {painMode === 'composite' ? (
        <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
          <span
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: TREND_CHART_COLORS.pain }}
          />
          PEG-3 composite
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
          {PAIN_ITEM_SERIES.map((seriesItem) => (
            <div key={seriesItem.yKey} className="flex items-center gap-1.5">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: seriesItem.color }}
              />
              {seriesItem.name}
            </div>
          ))}
        </div>
      )}
      <div className="flex overflow-hidden rounded-md border border-gray-200 bg-white text-[11px]">
        <button
          type="button"
          className={clsx('px-2.5 py-1', {
            'bg-white font-semibold text-gray-900': painMode === 'composite',
            'bg-transparent text-gray-500': painMode !== 'composite',
          })}
          onClick={() => onPainModeChange('composite')}
        >
          Composite
        </button>
        <button
          type="button"
          className={clsx('border-l border-gray-200 px-2.5 py-1', {
            'bg-white font-semibold text-gray-900': painMode === 'items',
            'bg-transparent text-gray-500': painMode !== 'items',
          })}
          onClick={() => onPainModeChange('items')}
        >
          3 items
        </button>
      </div>
    </div>
  );
}

export default function PatientTrendCharts({
  chartRows,
  patientTrend,
  painMode,
  onPainModeChange,
}: Props) {
  const lastCheckIn = patientTrend?.lastCheckIn;
  const painValues = getLastTwoNumericValues(chartRows, 'peg3Composite');
  const conditionValues = getLastTwoNumericValues(chartRows, 'conditionChangeRank');
  const adherenceValues = getLastTwoNumericValues(chartRows, 'carePlanAdherenceRank');
  const painCurrent = lastCheckIn?.peg3Composite ?? painValues.current;
  const conditionCurrent = lastCheckIn?.conditionChangeRank ?? conditionValues.current;
  const adherenceCurrent = lastCheckIn?.carePlanAdherenceRank ?? adherenceValues.current;
  const painTone = getPainTone(painCurrent);
  const conditionTone = getConditionTone(conditionCurrent);
  const adherenceTone = getAdherenceTone(adherenceCurrent);
  const painTrendLabel = getSeriesTrendLabel(
    painCurrent,
    lastCheckIn?.priorPeg3Composite ?? painValues.prior,
    'lowerBetter',
  );
  const conditionTrendLabel = getSeriesTrendLabel(
    conditionCurrent,
    conditionValues.prior,
    'higherBetter',
  );
  const adherenceTrendLabel = getSeriesTrendLabel(
    adherenceCurrent,
    adherenceValues.prior,
    'higherBetter',
  );
  const painCurrentLabel = isNil(painCurrent)
    ? DEFAULT_EMPTY_VALUE
    : String(painCurrent);
  const conditionCurrentLabel = lastCheckIn?.conditionChangeLabel
    ?? (isNil(conditionCurrent)
      ? DEFAULT_EMPTY_VALUE
      : CONDITION_RANK_LABELS[conditionCurrent] ?? String(conditionCurrent));
  const adherenceCurrentLabel = lastCheckIn?.carePlanAdherenceLabel
    ?? (isNil(adherenceCurrent)
      ? DEFAULT_EMPTY_VALUE
      : ADHERENCE_RANK_LABELS[adherenceCurrent] ?? String(adherenceCurrent));
  const timelineItems = getEventTimelineItems(patientTrend?.points);
  const suddenWorsening = patientTrend?.suddenWorsening;
  const totalEventsLabel = isNil(suddenWorsening?.totalEvents)
    ? DEFAULT_EMPTY_VALUE
    : String(suddenWorsening.totalEvents);
  const frequencyTrendLabel = suddenWorsening?.frequencyTrend
    ?? DEFAULT_EMPTY_VALUE;
  const mostRecentOnLabel = formatDate(suddenWorsening?.mostRecentOn ?? '');
  const mostRecentLabel = notEmptyString(mostRecentOnLabel)
    ? mostRecentOnLabel
    : DEFAULT_EMPTY_VALUE;
  const painSeries = painMode === 'items'
    ? PAIN_ITEM_SERIES.map(({ yKey, color, name }) => ({
      key: yKey,
      color,
      label: name,
    }))
    : [{
      key: 'peg3Composite',
      color: TREND_CHART_COLORS.pain,
      label: 'PEG-3',
    }];
  const conditionSeries = [{
    key: 'conditionChangeRank',
    color: TREND_CHART_COLORS.condition,
    label: 'Condition',
  }];
  const adherenceSeries = [{
    key: 'carePlanAdherenceRank',
    color: TREND_CHART_COLORS.adherence,
    label: 'Adherence',
  }];

  return chartRows.length === 0 ? (
    <div className="rounded-2xl border border-gray-200 bg-stone-50 p-6 text-center text-xs text-gray-500">
      No check-in trends yet
    </div>
  ) : (
    <div className="flex flex-col gap-3.5">
      <ChartCard
        title="Pain — Q1 PEG-3 · lower = better"
        badges={(
          <ChartStatusPills
            currentLabel={painCurrentLabel}
            trendLabel={painTrendLabel}
            theme="pain"
            tone={painTone}
          />
        )}
        rightSlot={(
          <PainModeToggle
            painMode={painMode}
            onPainModeChange={onPainModeChange}
          />
        )}
      >
        <TrendChart
          key={painMode}
          data={chartRows}
          xKey="completedOn"
          emptyText="No check-in trends yet"
          series={painSeries}
          yDomain={PAIN_Y_DOMAIN}
          yTicks={PAIN_Y_TICKS}
          tooltip={(
            <TrendTooltip
              xKey="completedOn"
              series={painSeries}
              formatValue={(_key, value) => String(value)}
            />
          )}
          axes
        />
      </ChartCard>

      <div className="rtm-lower-charts grid grid-cols-2 gap-3.5">
        <ChartCard
          title="Condition change — Q2"
          badges={(
            <ChartStatusPills
              currentLabel={conditionCurrentLabel}
              trendLabel={conditionTrendLabel}
              theme="condition"
              tone={conditionTone}
            />
          )}
        >
          <TrendChart
            data={chartRows}
            xKey="completedOn"
            emptyText="No check-in trends yet"
            series={conditionSeries}
            yDomain={CONDITION_Y_DOMAIN}
            yTicks={CONDITION_Y_TICKS}
            formatYTick={(value) => CONDITION_RANK_LABELS[value] ?? String(value)}
            yAxisWidth={140}
            referenceLineY={CONDITION_REFERENCE_Y}
            reversedYAxis
            tooltip={(
              <TrendTooltip
                xKey="completedOn"
                series={conditionSeries}
                formatValue={(_key, value) => CONDITION_RANK_LABELS[value] ?? String(value)}
              />
            )}
            axes
          />
        </ChartCard>

        <ChartCard
          title="Care plan adherence — Q3"
          badges={(
            <ChartStatusPills
              currentLabel={adherenceCurrentLabel}
              trendLabel={adherenceTrendLabel}
              theme="adherence"
              tone={adherenceTone}
            />
          )}
        >
          <TrendChart
            data={chartRows}
            xKey="completedOn"
            emptyText="No check-in trends yet"
            series={adherenceSeries}
            yDomain={ADHERENCE_Y_DOMAIN}
            yTicks={ADHERENCE_Y_TICKS}
            formatYTick={(value) => ADHERENCE_RANK_LABELS[value] ?? String(value)}
            yAxisWidth={200}
            referenceLineY={ADHERENCE_REFERENCE_Y}
            reversedYAxis
            tooltip={(
              <TrendTooltip
                xKey="completedOn"
                series={adherenceSeries}
                formatValue={(_key, value) => ADHERENCE_RANK_LABELS[value] ?? String(value)}
              />
            )}
            axes
          />
        </ChartCard>
      </div>

      <div className="flex flex-col gap-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">
          Sudden worsening
        </div>
        <div className="rtm-sudden-worsening-stats grid grid-cols-3 gap-3.5">
          <SuddenWorseningStatCard
            header="Total events"
            value={totalEventsLabel}
          />
          <SuddenWorseningStatCard
            header="Frequency trend"
            value={frequencyTrendLabel}
          />
          <SuddenWorseningStatCard
            header="Most recent"
            value={mostRecentLabel}
          />
        </div>
        <EventTimeline items={timelineItems} />
      </div>
    </div>
  );
}
